import React, { useMemo } from 'react';
import { View, StyleSheet } from 'react-native';
import { Text } from '@/components/common/text';
import { SVG_COMPONENTS } from '@/assets/admin';
import { FormSection } from '@/components';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { TabSegment, TabSegmentItem } from '@/components/shared/redesign';

type MessageTypeKey = 'groupChat' | 'singleChat' | 'sms';

interface MessageTypeSelectorProps {
  messageType: MessageTypeKey;
  onMessageTypeChange: (type: MessageTypeKey) => void;
  recipientCount: number;
}

interface MessageTypeOption {
  value: MessageTypeKey;
  label: string;
  description: string;
  icon: keyof typeof SVG_COMPONENTS;
}

export const MessageTypeSelector: React.FC<MessageTypeSelectorProps> = ({
  messageType,
  onMessageTypeChange,
  recipientCount,
}) => {
  const { redesign } = useAdminColors();

  const messageTypeOptions: MessageTypeOption[] = useMemo(() => [
    {
      value: 'singleChat',
      label: 'Conversation per recipient',
      description: `${recipientCount} conversations will be created, residents will not see each other responses`,
      icon: 'GROUP',
    },
    {
      value: 'sms',
      label: 'Text message thread',
      description: `${recipientCount} conversations will be created, residents will receive messages individually via SMS`,
      icon: 'MESSAGE_BLAST',
    },
    {
      value: 'groupChat',
      label: 'Group chat',
      description: 'One conversation will be created where all recipients can see each other responses',
      icon: 'EMPTY_MESSAGE',
    },
  ], [recipientCount]);

  const tabItems: TabSegmentItem<MessageTypeKey>[] = useMemo(() =>
    messageTypeOptions.map((opt) => ({ key: opt.value, label: opt.label })),
  [messageTypeOptions]);

  const selected = messageTypeOptions.find((o) => o.value === messageType) ?? messageTypeOptions[0];
  const SelectedIcon = SVG_COMPONENTS[selected.icon];

  return (
    <FormSection title="Message Type">
      <View style={styles.container}>
        <Text style={[styles.subtitle, { color: redesign.text.muted }]}>
          Choose how you want to send this message to recipients
        </Text>

        <TabSegment<MessageTypeKey>
          items={tabItems}
          selectedKey={messageType}
          onChange={onMessageTypeChange}
          variant="chip"
          size="md"
          style={styles.tabSegment}
        />

        <View style={styles.descriptionRow}>
          <View style={styles.iconContainer}>
            <SelectedIcon
              width={20}
              height={20}
              color={redesign.accent.blueBg}
            />
          </View>
          <Text style={[styles.descriptionText, { color: redesign.text.muted }]}>
            {selected.description}
          </Text>
        </View>
      </View>
    </FormSection>
  );
};

const styles = StyleSheet.create({
  container: {
    marginTop: 8,
  },
  subtitle: {
    fontSize: 14,
    marginBottom: 16,
    lineHeight: 20,
  },
  tabSegment: {
    marginBottom: 12,
  },
  descriptionRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingHorizontal: 4,
    marginTop: 4,
  },
  iconContainer: {
    width: 28,
    alignItems: 'center',
    marginRight: 8,
    marginTop: 2,
  },
  descriptionText: {
    flex: 1,
    fontSize: 14,
    lineHeight: 20,
  },
});
